import { useMineInfo } from '@services/auth'
import { useGetSessionTree, useSessionTree } from '@services/session'
import { Input } from '@shared/innmaxUI/Form'
import Form from '@shared/innmaxUI/Form/Form'
import { find, isNil, pathOr } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type { ISession } from '@services/session'
import { IconSearch } from '@shared/icons'
import { MapType } from '@shared/innmaxMap/map/map.types'
import LayoutTree, {
  LayoutTreeInstance,
  TreeData,
} from '@shared/innmaxUI/LayoutTree'
import { LayoutTreeNodeProps } from '@shared/innmaxUI/LayoutTree/LayoutTreeNode'
import { useDashboardContext } from '../../Provider'
import MainSidebar from '../MainSidebar'

type FilterBarType = {
  visible: boolean
  isLoading: boolean
  close: () => void
  layoutTree?: LayoutTreeInstance
}

export default function FilterBar({
  visible,
  isLoading,
  close,
  layoutTree,
}: FilterBarType) {
  const { t } = useTranslation()

  const { setCurrentSession, currentSession } = useDashboardContext()

  const [defaultExpandIds, setDefaultExpandIds] = React.useState<number[]>([])

  const [keyword, setSearchKeyword] = React.useState<string | undefined>()

  const [treeData, setTreeData] = React.useState<TreeData[]>([])

  const { data } = useGetSessionTree()

  const { dataSource: mineInfo } = useMineInfo()

  const { baseLayer, byId } = useSessionTree()

  const [form] = Form.useForm()

  const mySessions = pathOr<ISession[]>([], ['sessionInfos'], mineInfo)

  const handleSessionSearch = React.useCallback(() => {
    setSearchKeyword(form.getFieldsValue()?.input)
  }, []) // eslint-disable-line

  const treeResult = React.useMemo(() => {
    const results: any[] = []
    const loop = (area: any) => {
      area.title.indexOf(keyword) > -1 && results.push(area)
      if (area.children.length) {
        return area?.children.map(loop)
      }
    }

    if (keyword) {
      treeData.map(loop)
    } else {
      return treeData
    }

    return results
  }, [keyword, treeData])

  const convertTreeData = React.useCallback(
    (
      sessions: any[],
      level: number,
      props?: Partial<LayoutTreeNodeProps>
    ): TreeData[] => {
      const treeData: TreeData[] = []
      sessions.forEach(session => {
        const treeNodeProps = props
        if (session.status === 1 && session.treeAvailable) {
          treeData.push({
            type: 'EMPTY',
            id: session.id,
            title: session.name,
            // hasLayerImage: !!session.layerFileUrl,
            hasLayerImage:
              session.type === MapType.Upload && !!session.layerFileUrl,
            hasPermission:
              mineInfo?.allSessionPermission ||
              mySessions.filter(s => s.id === session.id).length > 0,
            payload: session,
            children: convertTreeData(session.sessions, level + 1),
            fullSession: session?.fullSession,
            ...treeNodeProps,
          } as any)
        }
      })
      return treeData
    },
    [mineInfo]
  )

  React.useEffect(() => {
    if (!data) {
      return
    }

    const baseLayer = find(
      (session: any) => session.status === 1 && session.id !== 1
    )(pathOr([], ['data'], data))

    if (isNil(baseLayer)) {
      setTreeData([])
      return
    }

    const treeDatas = convertTreeData([baseLayer], 1, {
      type: 'EMPTY',
    })

    if (treeDatas.length > 0) {
      setDefaultExpandIds([treeDatas[0].id])
    }

    setTreeData(treeDatas)
  }, [data]) // eslint-disable-line

  return (
    <MainSidebar visible={visible} onClose={close}>
      <StyledDialog className="flex-1 relative">
        <Form
          form={form}
          className="p-16"
          layout="vertical"
          hideRequiredMark
          colon={false}>
          <Form.Item name="input" className="mb-32">
            <StyledInputSearch
              className="w-fulll"
              placeholder={t('session:search layer')}
              allowClear
              suffix={
                <IconSearch
                  className="cursor-pointer"
                  onClick={handleSessionSearch}
                />
              }
            />
          </Form.Item>
        </Form>
        <StyledLayoutTreeWrapper>
          <LayoutTree
            hideLayerImageIcon
            defaultExpandIds={[baseLayer.id]}
            treeData={treeResult}
            onSelect={payload => {
              if (!payload) {
                return
              }
              if (payload.type === MapType.TREE) {
                return
              }
              if (payload.id === currentSession?.id) {
                return
              }
              if (payload.id === baseLayer.id) {
                setCurrentSession(baseLayer)
                return
              }
              if (byId[payload.id]) {
                setCurrentSession(byId[payload.id])
                return
              }
              console.error("can't found the specific session")
            }}
            className="px-16"
            showSelectedItem
            layoutTree={layoutTree}
          />
        </StyledLayoutTreeWrapper>
      </StyledDialog>
    </MainSidebar>
  )
}

const StyledDialog = styled.div`
  max-width: 240px;
  box-shadow: -6px 0 10px 0 rgb(0 0 0 / 10%);
`

const LevelHintSpan = styled.span`
  display: none;
  font-size: 12px;
  color: ${p => p.theme.darkGrey};

  button > span {
    text-decoration: underline;
  }

  .highlight {
    font-weight: 600;
    margin: 0px 3px;
  }
`

const StyledInputSearch = styled(Input)`
  border-radius: 4px;
`

const StyledLayoutTreeWrapper = styled.div`
  .node:hover {
    ${LevelHintSpan} {
      display: inline-block !important;
    }
  }
`
